import { useEffect, useState } from 'react';
import { ToastSwal } from '../../toastSwals/toastSwal';

export const useDropZoneField = (props: {
    accept: string;
    currentFiles?: { id: number; file: any }[];
    onChange: (files: File[]) => void;
    maxFileSize?: number;
}) => {
    const { accept, currentFiles, onChange, maxFileSize } = props;
    const [files, setFiles] = useState<{ id: number; file: any }[]>([]);
    const [imageSrc, setImageSrc] = useState<string | undefined>(undefined);
    const [videoSrc, setVideoSrc] = useState<string | undefined>(undefined);

    const getAcceptedExtensions = () =>
        accept?.split(',').map((ext) => ext.trim().toLowerCase()) || [];

    const isFileAccepted = (fileName: string) => {
        const fileExtension = `.${fileName.split('.').pop()?.toLowerCase()}`;
        return getAcceptedExtensions().includes(fileExtension);
    };

    const isFileSizeValid = (fileSize: number) => {
        return maxFileSize ? fileSize <= maxFileSize : true;
    };

    const updateFiles = (incomingFiles: any[]) => {
        const validFiles: any[] = [];
        const invalidFiles: { file: any; type: string }[] = [];  
        incomingFiles.forEach((file) => {
            if (!isFileAccepted(file.file.name)) {
                invalidFiles.push({
                    file: file.file.name,
                    type: 'file-invalid-type',
                });
            } else if (!isFileSizeValid(file.file.size)) {
                invalidFiles.push({
                    file: file.file.name,
                    type: 'file-too-large',
                });
            } else {
                
                validFiles.push(file);
            }
        });

        const MAX_TOTAL_SIZE = maxFileSize || 30 * 1024 * 1024;
        const newTotalSize = validFiles.reduce(
            (acc, f) => acc + (f.file?.size || 0),
            0
        );
        if (newTotalSize > MAX_TOTAL_SIZE) {
            ToastSwal.fire({
                text: `La suma total de los archivos no puede superar los 30MB.`,
                icon: 'error',
                timer: 5000,
                position: 'center-end',
                customClass: {
                    container: 'swal-high-zindex',
                },
            });
            return;
        }

        invalidFiles.forEach((invalid) => {
            if (invalid.type === 'file-invalid-type') {
                ToastSwal.fire({
                    text: `El archivo "${invalid.file}" tiene un tipo no permitido.`,
                    icon: 'error',
                    timer: 5000,
                    position: 'center-end',
                    customClass: {
                        container: 'swal-high-zindex',
                    },
                });
            } else if (invalid.type === 'file-too-large') {
                ToastSwal.fire({
                    text: `El archivo "${invalid.file}" es demasiado grande.`,
                    icon: 'error',
                    timer: 5000,
                    position: 'center-end',
                    customClass: {
                        container: 'swal-high-zindex',
                    },
                });
            } else {
                ToastSwal.fire({
                    text: `Error desconocido al cargar el archivo "${invalid.file}".`,
                    icon: 'error',
                    timer: 5000,
                    position: 'center-end',
                    customClass: {
                        container: 'swal-high-zindex',
                    },
                });
            }
        });

        const updatedFiles = validFiles;

        setFiles(updatedFiles);
        onChange(updatedFiles.map((f) => f.file));
    };

    const removeFile = (id: number) => {
        const updatedFiles = files.filter((file) => file.id !== id);
        setFiles(updatedFiles);
        onChange(updatedFiles.map((f) => f.file));
    };

    const handleSee = (imageSource: string | undefined) => {
        setImageSrc(imageSource);
    };

    const handleWatch = (videoSource: string | undefined) => {
        setVideoSrc(videoSource);
    };

    useEffect(() => {
        if (currentFiles) {
            setFiles(currentFiles.map((file, index) => ({ id: index, file })));
        } else {
            setFiles([]);
        }
    }, [currentFiles]);

    const getTotalSizeMB = () => {
        const total = files.reduce((acc, f) => acc + (f.file?.size || 0), 0);
        return (total / 1024 / 1024).toFixed(2);
    };
    return {
        files,
        imageSrc,
        videoSrc,
        updateFiles,
        removeFile,
        handleSee,
        handleWatch,
        setImageSrc,
        setVideoSrc,
        getTotalSizeMB,
    };
};
